import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  type: string;
  risk: string;
}

interface Link {
  source: string;
  target: string;
  type: string;
}

interface ForceDirectedGraphProps {
  nodes: Node[];
  links: Link[];
  width: number;
  height: number;
}

export const ForceDirectedGraph: React.FC<ForceDirectedGraphProps> = ({
  nodes,
  links,
  width,
  height
}) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    // Clear any existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    // Create the SVG container
    const svg = d3.select(svgRef.current)
      .attr("viewBox", [0, 0, width, height])
      .attr("width", width)
      .attr("height", height);

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        container.attr("transform", event.transform);
      });

    svg.call(zoom);

    // Create a container for the graph
    const container = svg.append("g");

    // Define arrow markers for different link types
    svg.append("defs").selectAll("marker")
      .data(["contains", "resolves_to", "exposes", "vulnerable_to"])
      .join("marker")
      .attr("id", d => `arrow-${d}`)
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 25)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("fill", "#4B5563")
      .attr("d", "M0,-5L10,0L0,5");

    // Create the simulation with proper node and link data
    const simulation = d3.forceSimulation(nodes)
      .force("link", d3.forceLink(links)
        .id((d: any) => d.id)
        .distance(150))
      .force("charge", d3.forceManyBody().strength(-1000))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius(60));

    // Create the links
    const link = container.append("g")
      .selectAll("line")
      .data(links)
      .join("line")
      .attr("stroke", "#4B5563")
      .attr("stroke-width", 1.5)
      .attr("marker-end", d => `url(#arrow-${d.type})`);

    // Create the nodes
    const node = container.append("g")
      .selectAll("g")
      .data(nodes)
      .join("g")
      .call(d3.drag<any, Node>()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended));

    // Add circles for nodes
    node.append("circle")
      .attr("r", 20)
      .attr("fill", d => getNodeColor(d.risk))
      .attr("stroke", "#1F2937")
      .attr("stroke-width", 2);

    // Add icons based on node type
    node.append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("fill", "white")
      .attr("font-family", "lucide")
      .attr("font-size", "14px")
      .text(d => getNodeIcon(d.type));

    // Add labels
    node.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", 30)
      .attr("fill", "#D1D5DB")
      .attr("font-size", "12px")
      .text(d => d.label);

    // Add tooltips
    node.append("title")
      .text(d => `${d.label}\nType: ${d.type}\nRisk: ${d.risk}`);

    // Update positions on each tick
    simulation.on("tick", () => {
      link
        .attr("x1", d => (d.source as any).x)
        .attr("y1", d => (d.source as any).y)
        .attr("x2", d => (d.target as any).x)
        .attr("y2", d => (d.target as any).y);

      node
        .attr("transform", d => `translate(${d.x},${d.y})`);
    });

    // Drag functions
    function dragstarted(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return () => {
      simulation.stop();
    };
  }, [nodes, links, width, height]);

  // Helper function to get node color based on risk
  const getNodeColor = (risk: string) => {
    switch (risk) {
      case 'critical': return '#EF4444'; // red-500
      case 'high': return '#F97316'; // orange-500
      case 'medium': return '#EAB308'; // yellow-500
      case 'low': return '#10B981'; // emerald-500
      default: return '#6B7280'; // gray-500
    }
  };

  // Helper function to get node icon based on type
  const getNodeIcon = (type: string) => {
    switch (type) {
      case 'domain': return '🌐';
      case 'subdomain': return '🔗';
      case 'ip': return '📡';
      case 'port': return '🔌';
      case 'vulnerability': return '⚠️';
      default: return '•';
    }
  };

  return (
    <svg
      ref={svgRef}
      className="w-full h-full"
      style={{ background: 'transparent' }}
    />
  );
};